import {
  AlertCircle,
  Calculator,
  CheckCircle,
  Link as LinkIcon,
  Loader2,
  Rocket,
  Sparkles,
  Tag,
  Upload,
  User,
  X,
  Zap,
} from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import PopularModelsCarousel from '../components/PopularModelsCarousel';
import STLDropzone from '../components/STLDropzone';
import { useAuth } from '../contexts/AuthContext';
import { scrapeModel } from '../services/apiService';
import type {
  AppliedPromoCode,
  Color,
  PopularModel,
  STLAnalysis,
  ScrapedModelData,
} from '../types';
import { calculatePriceFromEstimate, estimatePrint } from '../utils/printEstimator';

const NewOrder: React.FC = () => {
  const { user, setShowAuthModal } = useAuth();
  const [inputMode, setInputMode] = useState<'url' | 'stl'>('url');
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modelData, setModelData] = useState<ScrapedModelData | null>(null);
  const [selectedColor, setSelectedColor] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [price, setPrice] = useState<number>(0);
  const [colors, setColors] = useState<Color[]>([]);
  const [stlFile, setStlFile] = useState<File | null>(null);
  const [stlAnalysis, setStlAnalysis] = useState<STLAnalysis | null>(null);

  // Promo code state
  const [promoInput, setPromoInput] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoData, setPromoData] = useState<AppliedPromoCode | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);

  // Fetch available colors from API
  useEffect(() => {
    const fetchColors = async () => {
      try {
        const response = await fetch('/api/colors');
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        setColors(data.colors || []);
      } catch (err) {
        console.error('Failed to fetch colors:', err);
        setError('Could not load color options. Please refresh the page.');
      }
    };
    fetchColors();
  }, []);

  const handleScrape = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url) return;

    setLoading(true);
    setError(null);
    setModelData(null);
    setPrice(0);

    try {
      const data = await scrapeModel(url);
      setModelData(data);
    } catch (err) {
      console.error('Scrape failed:', err);
      setError('Could not fetch model info. Please check the URL and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPopularModel = (model: PopularModel) => {
    setInputMode('url');
    setUrl(model.modelUrl);
    setModelData({
      name: model.name,
      printTime: 0,
      filamentGrams: 0,
      imageUrl: model.imageUrl,
    });
    setPrice(model.price);
    setStlFile(null);
    setStlAnalysis(null);
    setError(null);
  };

  const handleSTLAnalyzed = (analysis: STLAnalysis, file: File) => {
    setStlFile(file);
    setStlAnalysis(analysis);

    // Estimate print parameters from STL
    const estimate = estimatePrint(analysis);

    // Create model data from STL estimate
    setModelData({
      name: file.name.replace(/\.stl$/i, ''),
      printTime: estimate.printTimeHours,
      filamentGrams: estimate.filamentGrams,
      imageUrl: 'https://placehold.co/400x300/3b82f6/ffffff?text=STL+Upload',
    });

    // Calculate price
    const calculatedPrice = calculatePriceFromEstimate(estimate);
    setPrice(calculatedPrice);
  };

  const isPromoType = (v: unknown): v is 'percent' | 'fixed' => v === 'percent' || v === 'fixed';

  const handleApplyPromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    setPromoLoading(true);
    setPromoError(null);
    setPromoData(null);
    try {
      const res = await fetch('/api/promo/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (data.valid && isPromoType(data.type)) {
        setPromoData({ code: data.code, type: data.type, value: data.value });
      } else {
        setPromoError(data.error || 'Invalid promo code');
      }
    } catch (err) {
      console.error('Promo validation request failed:', err);
      setPromoError('Could not validate promo code');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setPromoData(null);
    setPromoInput('');
    setPromoError(null);
  };

  const discount =
    promoData && price > 0
      ? promoData.type === 'percent'
        ? Math.round(price * (promoData.value / 100) * 100) / 100
        : Math.min(promoData.value, price)
      : 0;
  const finalPrice = Math.max(0, price - discount);

  const handleSubmitOrder = async () => {
    if (!modelData || !selectedColor) return;

    // Require authentication
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('sessionToken');
      const response = await fetch('/api/orders/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          modelUrl: url || `stl-upload://${stlFile?.name || 'unknown'}`,
          modelName: modelData.name,
          modelImageUrl: modelData.imageUrl,
          colorId: selectedColor,
          printTimeHours: modelData.printTime,
          filamentGrams: modelData.filamentGrams,
          price: price,
          promoCode: promoData?.code ?? undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit order');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit order');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <div className="max-w-md w-full p-8 glass rounded-3xl text-center relative overflow-hidden">
          {/* Confetti background effect */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute w-2 h-2 rounded-full animate-float"
                style={{
                  backgroundColor: ['#60a5fa', '#a78bfa', '#f472b6', '#4ade80', '#fbbf24'][i % 5],
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                  opacity: 0.6,
                }}
              />
            ))}
          </div>

          <div className="relative z-10">
            <div className="w-20 h-20 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce shadow-lg shadow-green-500/50">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-black mb-2" style={{ color: 'var(--text-primary)' }}>
              <span className="text-gradient">Awesome!</span>
            </h2>
            <p className="mb-2" style={{ color: 'var(--text-secondary)' }}>
              Your order is in the queue! We'll email you the final price soon.
            </p>
            <p className="text-xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
              "{modelData?.name}"
            </p>
            <button
              onClick={() => {
                setSubmitted(false);
                setUrl('');
                setModelData(null);
                setSelectedColor(null);
                setStlFile(null);
                setStlAnalysis(null);
                setError(null);
                setPromoInput('');
                setPromoData(null);
                setPromoError(null);
              }}
              className="btn-neon"
            >
              <Rocket className="w-5 h-5 inline mr-2" />
              Start New Order
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-8 px-4 relative">
      {/* Background decorative orbs */}
      <div className="bg-orb w-96 h-96 bg-blue-500 -top-48 -left-48" />
      <div
        className="bg-orb w-64 h-64 bg-purple-500 top-1/2 -right-32"
        style={{ animationDelay: '2s' }}
      />
      <div
        className="bg-orb w-48 h-48 bg-pink-500 bottom-0 left-1/4"
        style={{ animationDelay: '4s' }}
      />

      {/* Popular Models Carousel */}
      <div className="relative z-10">
        <PopularModelsCarousel onSelectModel={handleSelectPopularModel} />
      </div>

      {/* Header */}
      <div className="mb-8 text-center relative z-10">
        <h1 className="text-4xl md:text-5xl font-black mb-4">
          <span className="text-gradient">Get Your</span>{' '}
          <span className="text-gradient">3D Print</span>{' '}
          <Sparkles className="inline w-8 h-8 text-yellow-400 animate-pulse" />
        </h1>
        <p className="text-lg" style={{ color: 'var(--text-muted)' }}>
          Paste a link from{' '}
          <a
            href="https://makerworld.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent-blue font-bold hover:underline"
          >
            MakerWorld
          </a>{' '}
          or{' '}
          <a
            href="https://www.printables.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent-orange font-bold hover:underline"
          >
            Printables
          </a>
          , or pick from{' '}
          <span className="text-accent-purple font-bold">Popular Models</span> above!
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 relative z-10">
        {/* Left Column: Input */}
        <div className="lg:col-span-2 space-y-6">
          {/* Step 1: Choose Input Method */}
          <div className="glass rounded-2xl p-6" style={{ borderColor: 'var(--border-color)' }}>
            <h2
              className="text-lg font-bold mb-4 flex items-center"
              style={{ color: 'var(--text-primary)' }}
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center mr-3 text-sm font-black text-on-gradient">
                1
              </div>
              Get Your Model
            </h2>

            {/* Mode Toggle */}
            <div className="flex gap-3 mb-6">
              <button
                type="button"
                onClick={() => setInputMode('url')}
                className={`
                  flex-1 px-4 py-3 rounded-xl font-bold transition-all duration-200 flex items-center justify-center gap-2
                  ${
                    inputMode === 'url'
                      ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-on-gradient shadow-lg scale-[1.02]'
                      : 'surface-inactive hover:surface-inactive-hover'
                  }
                `}
              >
                <LinkIcon className="w-5 h-5" />
                Paste Link
              </button>
              <button
                type="button"
                onClick={() => setInputMode('stl')}
                className={`
                  flex-1 px-4 py-3 rounded-xl font-bold transition-all duration-200 flex items-center justify-center gap-2
                  ${
                    inputMode === 'stl'
                      ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-on-gradient shadow-lg scale-[1.02]'
                      : 'surface-inactive hover:surface-inactive-hover'
                  }
                `}
              >
                <Upload className="w-5 h-5" />
                Upload Model File
              </button>
            </div>

            {/* URL Input Mode */}
            {inputMode === 'url' && (
              <form onSubmit={handleScrape} className="flex gap-3">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://makerworld.com/en/models/..."
                  className="flex-1 theme-input rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none transition-all"
                  required
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-neon flex items-center gap-2 disabled:opacity-50"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Zap className="w-5 h-5" />
                  )}
                  {loading ? 'Fetching...' : 'Go!'}
                </button>
              </form>
            )}

            {/* STL Upload Mode */}
            {inputMode === 'stl' && <STLDropzone onFileAnalyzed={handleSTLAnalyzed} />}

            {/* Error Display */}
            {error && (
              <div className="mt-4 p-4 bg-red-500/20 border border-red-500/50 text-error rounded-xl text-sm flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                {error}
              </div>
            )}
          </div>

          {/* Step 2: Customize */}
          {modelData && (
            <div
              className="glass rounded-2xl p-6 animate-fade-in"
              style={{ borderColor: 'var(--border-color)' }}
            >
              <h2
                className="text-lg font-bold mb-4 flex items-center"
                style={{ color: 'var(--text-primary)' }}
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center mr-3 text-sm font-black text-on-gradient">
                  2
                </div>
                <Calculator className="w-5 h-5 mr-2 text-accent-purple" />
                Customize Your Print
              </h2>

              {/* Model Preview */}
              <div className="flex flex-col md:flex-row gap-6 mb-6">
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity" />
                  <img
                    src={modelData.imageUrl}
                    alt="Model Preview"
                    className="relative w-40 h-40 object-cover rounded-2xl border-2 border-white/20"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-xl mb-3" style={{ color: 'var(--text-primary)' }}>
                    {modelData.name}
                  </h3>
                  <div className={`grid ${stlAnalysis ? 'grid-cols-3' : 'grid-cols-2'} gap-4`}>
                    <div
                      className="rounded-xl p-3 text-center"
                      style={{ backgroundColor: 'var(--surface-inactive)' }}
                    >
                      <p
                        className="text-xs uppercase tracking-wide"
                        style={{ color: 'var(--text-muted)' }}
                      >
                        Print Time
                      </p>
                      <p className="text-2xl font-black text-accent-blue">{modelData.printTime}h</p>
                    </div>
                    <div
                      className="rounded-xl p-3 text-center"
                      style={{ backgroundColor: 'var(--surface-inactive)' }}
                    >
                      <p
                        className="text-xs uppercase tracking-wide"
                        style={{ color: 'var(--text-muted)' }}
                      >
                        Filament
                      </p>
                      <p className="text-2xl font-black text-accent-purple">
                        {modelData.filamentGrams}g
                      </p>
                    </div>
                    {stlAnalysis && (
                      <div
                        className="rounded-xl p-3 text-center"
                        style={{ backgroundColor: 'var(--surface-inactive)' }}
                      >
                        <p
                          className="text-xs uppercase tracking-wide"
                          style={{ color: 'var(--text-muted)' }}
                        >
                          Size (mm)
                        </p>
                        <p className="text-sm font-black text-success">
                          {(stlAnalysis.boundingBox.x * 10).toFixed(1)} ×{' '}
                          {(stlAnalysis.boundingBox.y * 10).toFixed(1)} ×{' '}
                          {(stlAnalysis.boundingBox.z * 10).toFixed(1)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Color Selector */}
              <div className="mb-6">
                <label
                  className="block text-sm font-bold mb-3"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Pick Your Color
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {colors.map((color) => (
                    <button
                      key={color.id}
                      onClick={() => setSelectedColor(color.id)}
                      className={`
                        flex items-center p-3 rounded-xl border-2 transition-all duration-200
                        ${
                          selectedColor === color.id
                            ? 'border-blue-500 bg-blue-500/10 scale-105 shadow-lg'
                            : 'hover:bg-slate-50 dark:hover:bg-white/5'
                        }
                      `}
                    >
                      <span
                        className="w-6 h-6 rounded-full mr-3 shadow-lg ring-2 ring-white/20"
                        style={{ backgroundColor: color.hex }}
                      />
                      <span
                        className="text-sm font-medium"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        {color.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* User Info / Sign In Prompt */}
              <div className="mb-6">
                {user ? (
                  <div
                    className="flex items-center gap-3 p-4 rounded-xl border border-green-500/30"
                    style={{ backgroundColor: 'var(--surface-inactive)' }}
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
                      <User className="w-5 h-5 text-on-gradient" />
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                        Ordering as
                      </p>
                      <p className="font-bold" style={{ color: 'var(--text-primary)' }}>
                        {user.name}
                      </p>
                    </div>
                    {user.isGuest && (
                      <span className="ml-auto text-xs bg-yellow-500/20 text-warning px-2 py-1 rounded-lg">
                        Guest
                      </span>
                    )}
                  </div>
                ) : (
                  <div
                    className="p-4 rounded-xl border border-blue-500/30"
                    style={{ backgroundColor: 'var(--surface-inactive)' }}
                  >
                    <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
                      Sign in or continue as guest to place your order
                    </p>
                    <button
                      onClick={() => setShowAuthModal(true)}
                      className="text-accent-blue font-bold text-sm hover:opacity-80 transition-colors"
                    >
                      Sign in / Guest checkout
                    </button>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <button
                onClick={handleSubmitOrder}
                disabled={!selectedColor || loading}
                className="w-full btn-neon text-lg disabled:opacity-30 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  <Loader2 className="w-6 h-6 inline mr-2 animate-spin" />
                ) : (
                  <Rocket className="w-6 h-6 inline mr-2" />
                )}
                {loading ? 'Submitting...' : user ? 'Submit Order' : 'Sign In to Order'}
              </button>
            </div>
          )}
        </div>

        {/* Right Column: Price Card */}
        <div className="lg:col-span-1">
          <div
            className="glass rounded-2xl p-6 sticky top-24 text-center"
            style={{ borderColor: 'var(--border-color)' }}
          >
            <h3
              className="text-sm font-bold uppercase tracking-wider mb-2"
              style={{ color: 'var(--text-muted)' }}
            >
              Estimated Price
            </h3>

            <div className="relative py-6">
              {modelData ? (
                <>
                  {promoData && discount > 0 ? (
                    <>
                      <div
                        className="text-2xl font-black line-through mb-1"
                        style={{ color: 'var(--text-muted)' }}
                      >
                        ~${price.toFixed(2)}
                      </div>
                      <div className="price-tag text-success">~${finalPrice.toFixed(2)}</div>
                      <div className="mt-2 inline-flex items-center gap-1 px-3 py-1 bg-green-500/20 border border-green-500/30 rounded-full">
                        <Tag className="w-3 h-3 text-success" />
                        <span className="text-success text-xs font-bold">
                          {promoData.type === 'percent'
                            ? `${promoData.value}% off`
                            : `$${promoData.value} off`}{' '}
                          applied
                        </span>
                      </div>
                    </>
                  ) : price > 0 ? (
                    <>
                      <div className="price-tag text-success">~${price.toFixed(2)}</div>
                      <p className="text-sm mt-2" style={{ color: 'var(--text-muted)' }}>
                        We'll confirm the exact price with you!
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="text-3xl font-black text-accent-blue">Quote Pending</div>
                      <p className="text-sm mt-2" style={{ color: 'var(--text-muted)' }}>
                        We'll email you a price before printing!
                      </p>
                    </>
                  )}
                </>
              ) : (
                <>
                  <div className="text-5xl font-black" style={{ color: 'var(--text-muted)' }}>
                    $?.??
                  </div>
                  <p className="text-sm mt-4" style={{ color: 'var(--text-muted)' }}>
                    Paste a link or pick a popular model!
                  </p>
                </>
              )}
            </div>

            {/* Promo Code */}
            <div
              className="mb-4"
              style={{ borderTop: '1px solid var(--divider-color)', paddingTop: '1rem' }}
            >
              <p
                className="text-xs font-bold uppercase tracking-wider mb-2 text-left"
                style={{ color: 'var(--text-muted)' }}
              >
                Promo Code
              </p>
              {promoData ? (
                <div className="flex items-center justify-between px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-success" />
                    <span className="font-mono font-bold text-success text-sm">
                      {promoData.code}
                    </span>
                  </div>
                  <button
                    onClick={handleRemovePromo}
                    className="p-1 rounded-lg hover:bg-red-500/20 transition-colors"
                    title="Remove promo code"
                  >
                    <X className="w-4 h-4 text-error" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={promoInput}
                      onChange={(e) => {
                        setPromoInput(e.target.value.toUpperCase());
                        setPromoError(null);
                      }}
                      onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
                      placeholder="SUMMER10"
                      className="flex-1 theme-input rounded-xl px-3 py-2 text-sm font-mono uppercase focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    <button
                      onClick={handleApplyPromo}
                      disabled={!promoInput.trim() || promoLoading}
                      className="px-3 py-2 bg-purple-500/20 text-accent-purple rounded-xl text-sm font-bold hover:bg-purple-500/30 transition-colors disabled:opacity-50"
                    >
                      {promoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                    </button>
                  </div>
                  {promoError && (
                    <p className="text-error text-xs mt-1 text-left flex items-center gap-1">
                      <AlertCircle className="w-3 h-3 flex-shrink-0" />
                      {promoError}
                    </p>
                  )}
                </>
              )}
            </div>

            <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-xl">
              <p className="text-success text-sm font-bold">Most prints are under $5!*</p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                We'll email you the final price before printing.
              </p>
              <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
                *Based on prints under 3 hours and 50g of material, which covers about 80% of
                orders.
              </p>
            </div>

            {/* Fun facts */}
            <div className="mt-6 pt-6" style={{ borderTop: '1px solid var(--divider-color)' }}>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Powered by <span className="text-accent-blue">Bambu Lab P1</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewOrder;
